import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import dynamic from 'next/dynamic'
import { bindActionCreators } from 'redux'
import CommonLayout from 'components/layout/common'
import UserOptions from 'components/user/user-option'
import DynamicLoader from 'components/dynamic-loader'
import { withI18next } from 'lib/i18n/withI18next'
import StateStorage from 'lib/state-storage'
import authDuck from 'components/auth/duck'
import { USER_PAGE_SLUGS } from 'constants/user'
import { PAGE_NAME } from 'pages/const'
import Error from 'pages/_error'
import BasePage from '../base'
import styles from './style.scss'

const DynamicAccountDetails = dynamic(() => import('./account-details'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicCredits = dynamic(() => import('./pomelo-credit'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicCreditCards = dynamic(() => import('./credit-cards'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicMyLoyalty = dynamic(() => import('./my-loyalty/MyLoyalty'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicOrders = dynamic(() => import('./orders'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicOrder = dynamic(() => import('./order'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicAddressBook = dynamic(() => import('components/address-book'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicReturns = dynamic(() => import('./returns'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicReturn = dynamic(() => import('./return'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicNotificationCenter = dynamic(
  () => import('./notification-center'),
  { ssr: false, loading: DynamicLoader },
)
const DynamicUserQRInfo = dynamic(() => import('./user-qr-info'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicMyAccount = dynamic(() => import('./my-account/MyAccountPage'), {
  ssr: false,
  loading: DynamicLoader,
})
const DynamicMyRewards = dynamic(
  () => import('./my-rewards-page/MyRewardsPage'),
  {
    ssr: false,
    loading: DynamicLoader,
  },
)

class User extends BasePage {
  static getInitialProps({ store, query, req, res, isServer }) {
    const { id, page, is_offline, ref_type } = query

    if (isServer) {
      BasePage.loadInitialContentFromServer({ req, res, store })
    } else {
      BasePage.loadInitialContentFromClient(req, res, store)
    }

    return {
      page,
      id,
      is_offline: Boolean(is_offline),
      ref_type,
    }
  }

  childComponent = null

  state = {
    page: null,
  }

  getHasAuthUser() {
    return !!StateStorage.getAuthUser()
  }

  afterPageMount() {
    this.loadKlinkScript()

    if (!this.getHasAuthUser()) {
      this.props.updateShowLogin(true)
    }
  }

  componentDidUpdate() {
    this.childComponent = this.getChildComponent()
  }

  getChildComponent = () => {
    const { id, isPhone, page, is_offline, ref_type } = this.props

    switch (page) {
      case USER_PAGE_SLUGS.creditCards:
        return <DynamicCreditCards />
      case USER_PAGE_SLUGS.pomeloCredits:
        return <DynamicCredits isPhone={isPhone} />
      case USER_PAGE_SLUGS.addresses:
        return <DynamicAddressBook />
      case USER_PAGE_SLUGS.ordersHistory:
        return <DynamicOrders />
      case USER_PAGE_SLUGS.orderDetails:
        return <DynamicOrder reference={id} is_offline={is_offline} />
      case USER_PAGE_SLUGS.returnsHistory:
        return <DynamicReturns />
      case USER_PAGE_SLUGS.returnDetails:
        return <DynamicReturn reference={id} />
      case USER_PAGE_SLUGS.notifications:
        return <DynamicNotificationCenter />
      case USER_PAGE_SLUGS.pomeloID:
        return <DynamicUserQRInfo />
      case USER_PAGE_SLUGS.myloyalty:
        return <DynamicMyLoyalty />
      case USER_PAGE_SLUGS.accountDetails:
        return <DynamicAccountDetails />
      case USER_PAGE_SLUGS.myRewards:
        return <DynamicMyRewards ref_type={ref_type} />
      case USER_PAGE_SLUGS.myAccount:
      default:
        return <DynamicMyAccount />
    }
  }

  isShouldRenderNotFoundPage() {
    const { isCreditEnable, page } = this.props
    return page === USER_PAGE_SLUGS.pomeloCredits && !isCreditEnable
  }

  render() {
    if (this.isShouldRenderNotFoundPage()) {
      return <Error />
    }

    const hasAuthUser = this.getHasAuthUser()
    const isShowUserOptions = hasAuthUser && this.props.page === 'my-account'

    return (
      <CommonLayout pageName={PAGE_NAME.user} showUsp={!this.props.isPhone}>
        <style jsx>{styles}</style>
        <div className="page-container-user">
          <div className="left-nav">
            {isShowUserOptions && <UserOptions currentPage={this.props.page} />}
          </div>
          <div className="right-container">
            {hasAuthUser && this.getChildComponent()}
          </div>
        </div>
      </CommonLayout>
    )
  }
}

User.propTypes = {
  isPhone: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  internationalization: PropTypes.shape({}).isRequired,
  isCreditEnable: PropTypes.bool.isRequired,
  updateShowLogin: PropTypes.func.isRequired,
}

const Extended = withI18next()(User)

export default connect(
  (state) => ({
    isPhone: state.device.isPhone,
    internationalization: state.internationalization,
    user: state.auth.user,
    isCreditEnable: state.kcg.isCreditEnable,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        updateShowLogin: authDuck.creators.updateShowLogin,
      },
      dispatch,
    ),
)(Extended)
